// scenes/BaseInteriorScene.js
import Phaser from "phaser";
import AudioManager from "../../../src/@shared/utils/AudioManager";
import { updatePlayerPresence } from "../../@shared/utils/roomFunctions";
import { createWalkAnimations, preloadAvatars } from "../../@shared/utils/SceneHelpers";
import cityNpc1IdlePng from "../../@shared/assets/npcs/spritesheets/FD_Character_001_Idle.png";
import cityNpc2IdlePng from "../../@shared/assets/npcs/spritesheets/FD_Character_002_Idle.png";
import cityNpc3IdlePng from "../../@shared/assets/npcs/spritesheets/FD_Character_003_Idle.png";
import cityNpc4IdlePng from "../../@shared/assets/npcs/spritesheets/FD_Character_007_Idle.png";
import cityNpc5IdlePng from "../../@shared/assets/npcs/spritesheets/FD_Character_016_Idle.png";
import {
  getAvatarSpriteConfig,
  getAvatarFrameForDirection,
  normalizeAvatarName,
  scaleAvatarBodyOffset,
  scaleAvatarBodySize,
  SPRITES_MAP,
} from "../../@shared/utils/SpritesMap";
import {
  createLocalOrbAvatarImage,
  getLocalOrbAvatarConfig,
  ORB_RENDER_SIZE,
  preloadLocalOrbAvatar,
  updateLocalOrbAvatar,
} from "../../@shared/utils/phaserOrbAvatar";
import { createOrbTextureKey, resolveAvatarData } from "../../@shared/utils/orbAvatar";
import {
  createStoryCompanionFollower,
  destroyStoryCompanionFollower,
  syncStoryCompanionFollower,
} from "../../@shared/utils/storyCompanionFollower";
import { applyWorldSceneBackground } from "../../@shared/utils/worldSceneBackgrounds";

const INTERIOR_HUMAN_SCALE_MULTIPLIER = 0.8;
const ORB_COLLISION_RADIUS = 20;
const ORB_COLLISION_Y_SCALE = 2.0;
const REMOTE_PLAYER_NAME_OFFSET_Y = 50;
const INTERIOR_ENTRANCE_SPAWN_OFFSET_Y = 34;
const INTERIOR_ENTRANCE_FACING = 180;
const INTERIOR_AMBIENT_NPC_FRAME_SIZE = 24;
const INTERIOR_AMBIENT_NPC_SCALE = 2.15;
const INTERIOR_AMBIENT_NPCS = [
  { key: "interiorAmbientNpcA", texture: cityNpc1IdlePng },
  { key: "interiorAmbientNpcB", texture: cityNpc2IdlePng },
  { key: "interiorAmbientNpcC", texture: cityNpc3IdlePng },
  { key: "interiorAmbientNpcD", texture: cityNpc4IdlePng },
  { key: "interiorAmbientNpcE", texture: cityNpc5IdlePng },
];

const destroyRemotePlayerVisual = (entry) => {
  entry?.sprite?.destroy?.();
  entry?.orbImage?.destroy?.();
  entry?.nameTag?.destroy?.();
  entry?.nameTagBg?.destroy?.();
};

export default class BaseInteriorScene extends Phaser.Scene {
  constructor(
    key,
    collisionData,
    backgroundImage = null,
    doors = {},
    getNpcDialogue = () => null,
    opts = {}
  ) {
    super({ key });
    this.collisionData = collisionData;
    this.backgroundImage = backgroundImage;
    this.doors = doors;
    this.getNpcDialogue = getNpcDialogue;

    // NEW: TMJ options
    this.tmj = opts.tmj || null;
    this.tmjLayer = opts.tmjLayer || "TestCollisions";
    this.debugColliders = false;
    this.mapSource = opts.mapSource || null;
    this.mapTilesetConfigs = opts.mapTilesetConfigs || {};
    this.mapLayerDepths = opts.mapLayerDepths || null;
    this.overlayMapSource = opts.overlayMapSource || null;
    this.overlayMapTilesetConfigs = opts.overlayMapTilesetConfigs || {};
    this.overlayMapLayerDepths = opts.overlayMapLayerDepths || null;
  }

  // NEW: turn Tiled JSON → { buildings: [{ name, polygon: [ [x,y], ... ] }] }
  static extractCollisionDataFromTiled(tiledJson, layerName = "TestCollisions") {
    const layer =
      tiledJson?.layers?.find(
        (l) => (l.type === "objectgroup" || l.type === "object") && l.name === layerName
      ) || null;

    if (!layer) return { buildings: [] };
    const toAbs = (o, pts) => pts.map((pt) => [o.x + pt.x, o.y + pt.y]);

    const buildings = (layer.objects || []).flatMap((o) => {
      if (Array.isArray(o.polygon)) {
        return [{ name: o.name || o.type || "", polygon: toAbs(o, o.polygon) }];
      }
      if (o.width && o.height) {
        const poly = [
          [o.x, o.y],
          [o.x + o.width, o.y],
          [o.x + o.width, o.y + o.height],
          [o.x, o.y + o.height],
        ];
        return [{ name: o.name || o.type || "", polygon: poly }];
      }
      if (Array.isArray(o.polyline)) {
        const pts = toAbs(o, o.polyline);
        const xs = pts.map((p) => p[0]);
        const ys = pts.map((p) => p[1]);
        const poly = [
          [Math.min(...xs), Math.min(...ys)],
          [Math.max(...xs), Math.min(...ys)],
          [Math.max(...xs), Math.max(...ys)],
          [Math.min(...xs), Math.max(...ys)],
        ];
        return [{ name: o.name || o.type || "", polygon: poly }];
      }
      return [];
    });

    return { buildings };
  }

  static extractCollisionDataFromMapSource(mapSource, layerName = "TestCollisions") {
    const layer =
      mapSource?.layers?.find(
        (entry) => entry.type === "objectgroup" && entry.name === layerName,
      ) || null;

    if (!layer) return { buildings: [] };

    const buildings = (layer.objects || []).flatMap((object) => {
      if (object.width && object.height) {
        return [{
          name: object.name || object.type || "",
          polygon: [
            [object.x, object.y],
            [object.x + object.width, object.y],
            [object.x + object.width, object.y + object.height],
            [object.x, object.y + object.height],
          ],
        }];
      }

      return [];
    });

    return { buildings };
  }

  getCollisionRectByName(name) {
    const object = this.collisionData?.buildings?.find(
      (entry) => entry.name === name,
    );
    if (!object?.polygon?.length) return null;

    const xs = object.polygon.map((point) => point[0]);
    const ys = object.polygon.map((point) => point[1]);

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  }

  getInteriorEntranceSpawn() {
    const exitRect = this.getCollisionRectByName("exit");
    if (!exitRect) return null;

    return {
      x: (exitRect.minX + exitRect.maxX) / 2,
      y: exitRect.minY - INTERIOR_ENTRANCE_SPAWN_OFFSET_Y,
      facingDirection: INTERIOR_ENTRANCE_FACING,
    };
  }

  resolveInitialPlayerSpawn(data = {}) {
    if (data.storySpawn || data.useExactSpawn) {
      return {
        ...(data.spawn || { x: 300, y: 300 }),
        facingDirection: data.facingDirection ?? data.spawn?.facingDirection ?? null,
      };
    }

    const entranceSpawn = this.getInteriorEntranceSpawn();
    if (entranceSpawn) return entranceSpawn;

    return {
      ...(data.spawn || { x: 300, y: 300 }),
      facingDirection: data.facingDirection ?? data.spawn?.facingDirection ?? null,
    };
  }

  preload() {
    const sceneData = this.scene.settings.data || {};
    const { avatarData, textureKey } = getLocalOrbAvatarConfig(sceneData?.userData);
    const avatarKey = normalizeAvatarName(
      sceneData?.heroAvatarKey || sceneData?.userData?.avatar?.name || "io_default"
    );
    const heroSpriteConfig = getAvatarSpriteConfig(avatarKey);
    const heroUrl = sceneData?.heroUrl || SPRITES_MAP[avatarKey] || SPRITES_MAP.io_default;

    this.heroAvatarKey = avatarKey;
    this.heroSpriteConfig = heroSpriteConfig;
    this.heroUrl = heroUrl;

    preloadAvatars(this, SPRITES_MAP);
    INTERIOR_AMBIENT_NPCS.forEach((npc) => {
      if (this.textures.exists(npc.key)) return;
      this.load.spritesheet(npc.key, npc.texture, {
        frameWidth: INTERIOR_AMBIENT_NPC_FRAME_SIZE,
        frameHeight: INTERIOR_AMBIENT_NPC_FRAME_SIZE,
      });
    });

    this.load.spritesheet("hero", heroUrl, {
      frameWidth: heroSpriteConfig.frameWidth,
      frameHeight: heroSpriteConfig.frameHeight,
    });
    preloadLocalOrbAvatar(this, avatarData, textureKey);

    const preloadTilesets = (mapSource, mapTilesetConfigs) => {
      if (!mapSource) return;
      const uniqueTilesets = new Map();
      (mapSource.tilesets || []).forEach((tileset) => {
        const config =
          mapTilesetConfigs[tileset.basename] ||
          mapTilesetConfigs[tileset.source];
        if (!config || uniqueTilesets.has(config.textureKey)) return;
        uniqueTilesets.set(config.textureKey, config);
      });

      uniqueTilesets.forEach((config) => {
        this.load.spritesheet(config.textureKey, config.image, {
          frameWidth: config.frameWidth,
          frameHeight: config.frameHeight,
        });
      });
    };

    preloadTilesets(this.mapSource, this.mapTilesetConfigs);
    preloadTilesets(this.overlayMapSource, this.overlayMapTilesetConfigs);

    if (this.backgroundImage) {
      const bgKey = this.scene.key + "_bg"; // 👈 Unique per scene
      if (this.textures.exists(bgKey)) {
        this.textures.remove(bgKey);
      }
      this.load.image(bgKey, this.backgroundImage);
    }

    // NEW: foreground
    if (this.foregroundImage) {
      const fgKey = this.scene.key + "_fg";
      if (this.textures.exists(fgKey)) {
        this.textures.remove(fgKey);
      }
      this.load.image(fgKey, this.foregroundImage);
    }
  }


  drawCollisionDebugBox(x, y, width, height, color = 0xffffff, alpha = 0.3) {
    const g = this.add.graphics();
    g.setDepth(9999).setScrollFactor(1);
    g.lineStyle(1, color, 1);
    g.fillStyle(color, alpha);
    g.strokeRect(x - width / 2, y - height / 2, width, height);
    g.fillRect(x - width / 2, y - height / 2, width, height);
  }

  drawPlayerDebugBox() {
    if (!this.player || !this.player.body || !this.playerDebugGfx) return;

    const b = this.player.body.bounds;
    const w = b.max.x - b.min.x;
    const h = b.max.y - b.min.y;
    const x = (b.min.x + b.max.x) / 2;
    const y = (b.min.y + b.max.y) / 2;

    this.playerDebugGfx.clear();
    // this.playerDebugGfx.lineStyle(2, 0xffff00, 1);   // yellow outline
    // this.playerDebugGfx.fillStyle(0xffff00, 0.15);   // translucent fill
    // this.playerDebugGfx.strokeRect(x - w / 2, y - h / 2, w, h);
    // this.playerDebugGfx.fillRect(x - w / 2, y - h / 2, w, h);
  }

  getSourceLayerByName(layerName) {
    return (
      this.mapSource?.layers?.find(
        (layer) => layer.type === "tilelayer" && layer.name === layerName,
      ) || null
    );
  }

  getOverlayLayerByName(layerName) {
    return (
      this.overlayMapSource?.layers?.find(
        (layer) => layer.type === "tilelayer" && layer.name === layerName,
      ) || null
    );
  }

  buildTilesetRegistryForSource(mapSource, mapTilesetConfigs) {
    if (!mapSource) return [];

    return (mapSource.tilesets || [])
      .map((tileset, index, array) => {
        const config =
          mapTilesetConfigs[tileset.basename] ||
          mapTilesetConfigs[tileset.source];
        if (!config) return null;

        const next = array[index + 1];
        return {
          firstgid: Number(tileset.firstgid),
          lastgid: next ? Number(next.firstgid) - 1 : Number.MAX_SAFE_INTEGER,
          textureKey: config.textureKey,
          frameWidth: config.frameWidth,
          frameHeight: config.frameHeight,
          columns: config.columns || Math.floor(config.imageWidth / config.frameWidth),
        };
      })
      .filter(Boolean);
  }

  ensureAmbientInteriorNpcAnimations() {
    INTERIOR_AMBIENT_NPCS.forEach((npc) => {
      ["down", "left", "right", "up"].forEach((dir, i) => {
        const idleKey = `${npc.key}-idle-${dir}`;
        if (!this.anims.exists(idleKey)) {
          this.anims.create({
            key: idleKey,
            frames: this.anims.generateFrameNumbers(npc.key, {
              start: 4 * i,
              end: 4 * i + 3,
            }),
            frameRate: 4,
            repeat: -1,
          });
        }
      });
    });
  }

  createAmbientInteriorNpc({
    textureKey = "interiorAmbientNpcA",
    x,
    y,
    facing = "down",
    scale = INTERIOR_AMBIENT_NPC_SCALE,
  }) {
    this.ensureAmbientInteriorNpcAnimations();
    const sprite = this.add
      .sprite(x, y, textureKey, 0)
      .setOrigin(0.5, 1)
      .setScale(scale)
      .setDepth(y + 18);

    const animKey = `${textureKey}-idle-${facing}`;
    if (this.anims.exists(animKey)) {
      sprite.play(animKey, true);
    }

    return sprite;
  }

  buildMapTilesetRegistry() {
    this.mapTilesetRegistry = this.buildTilesetRegistryForSource(
      this.mapSource,
      this.mapTilesetConfigs,
    );
  }

  buildOverlayTilesetRegistry() {
    this.overlayMapTilesetRegistry = this.buildTilesetRegistryForSource(
      this.overlayMapSource,
      this.overlayMapTilesetConfigs,
    );
  }

  resolveTilesetForGid(gid, registry = this.mapTilesetRegistry) {
    if (!gid || !registry) return null;

    const entry = registry.find(
      (tileset) => gid >= tileset.firstgid && gid <= tileset.lastgid,
    );
    if (!entry) return null;

    return {
      textureKey: entry.textureKey,
      frame: gid - entry.firstgid,
    };
  }

  drawStaticLayer(layer, depth, registry, texturePrefix = this.scene.key) {
    if (!layer) return;
    if (layer.visible === false || layer.visible === 0) return;

    const rt = this.make.renderTexture({
      width: this.mapPixelWidth,
      height: this.mapPixelHeight,
      add: false,
    });

    layer.data.forEach((gid, index) => {
      if (!gid) return;
      const resolved = this.resolveTilesetForGid(gid, registry);
      if (!resolved) return;

      const tx = index % layer.width;
      const ty = Math.floor(index / layer.width);
      rt.drawFrame(
        resolved.textureKey,
        resolved.frame,
        tx * this.mapTileWidth,
        ty * this.mapTileHeight,
      );
    });

    const textureKey = `${texturePrefix}-${layer.name}`;
    if (this.textures.exists(textureKey)) {
      this.textures.remove(textureKey);
    }
    rt.saveTexture(textureKey);
    this.add.image(0, 0, textureKey).setOrigin(0).setDepth(depth);
  }

  drawStaticLayerFromSource(layerName, depth) {
    this.drawStaticLayer(
      this.getSourceLayerByName(layerName),
      depth,
      this.mapTilesetRegistry,
      this.scene.key,
    );
  }

  drawStaticOverlayLayer(layerName, depth) {
    this.drawStaticLayer(
      this.getOverlayLayerByName(layerName),
      depth,
      this.overlayMapTilesetRegistry,
      `${this.scene.key}-overlay`,
    );
  }

  drawMapFromSource() {
    if (!this.mapSource) return;

    const defaultLayers = (this.mapSource.layers || [])
      .filter((layer) => layer.type === "tilelayer")
      .map((layer, index) => ({
        name: layer.name,
        depth: index + 1,
      }));
    const layersToDraw = this.mapLayerDepths || defaultLayers;

    layersToDraw.forEach(({ name, depth }) => {
      this.drawStaticLayerFromSource(name, depth);
    });
  }

  drawOverlayMapLayers() {
    if (!this.overlayMapSource) return;

    const defaultLayers = (this.overlayMapSource.layers || [])
      .filter((layer) => layer.type === "tilelayer")
      .map((layer, index) => ({
        name: layer.name,
        depth: 200 + index,
      }));
    const layersToDraw = this.overlayMapLayerDepths || defaultLayers;

    layersToDraw.forEach(({ name, depth }) => {
      this.drawStaticOverlayLayer(name, depth);
    });
  }

  create(data = {}) {
  console.log("DATA ==>", data);

  // Allow caller to override bg/collision data per usual
  if (data.backgroundImage) this.backgroundImage = data.backgroundImage;
  if (data.collisionData) this.collisionData = data.collisionData;
  if (data.exit && this.doors?.exit) {
    this.doors = {
      ...this.doors,
      exit: {
        ...this.doors.exit,
        spawnAt: data.exit.spawnAt || data.exit,
      },
    };
  }

  // NEW: If no collisionData but a TMJ was provided, derive it now
  if (!this.collisionData && this.tmj) {
    this.collisionData = BaseInteriorScene.extractCollisionDataFromTiled(
      this.tmj,
      this.tmjLayer
    );
  }
  if (!this.collisionData && this.mapSource) {
    this.collisionData = BaseInteriorScene.extractCollisionDataFromMapSource(
      this.mapSource,
      this.tmjLayer,
    );
  }

  const userData = data.userData;
  const { avatarData: resolvedLocalAvatar, textureKey: localOrbTextureKey } =
    getLocalOrbAvatarConfig(userData);
  const avatarKey = normalizeAvatarName(
    data.heroAvatarKey || userData?.avatar?.name || this.heroAvatarKey || "io_default"
  );
  const heroSpriteConfig =
    this.heroSpriteConfig || getAvatarSpriteConfig(avatarKey);
  const playerScale =
    (heroSpriteConfig.worldScale || 1) *
    (resolvedLocalAvatar?.isOrbAvatar ? 1 : INTERIOR_HUMAN_SCALE_MULTIPLIER);
  const heroBodySize = scaleAvatarBodySize(avatarKey, {
    width: 64,
    height: 108,
  });
  const heroBodyOffset = scaleAvatarBodyOffset(avatarKey, {
    x: 32,
    y: 104,
  });
  this.localOrbAvatarData = resolvedLocalAvatar;
  this.localOrbTextureKey = localOrbTextureKey;
  this.heroAvatarKey = avatarKey;
  this.heroSpriteConfig = heroSpriteConfig;
  const players = data.allData?.playersFromReact || {};
  const player = players[userData?.id];
  const building = data.building;

  this.playersFromReact = players || {};
  this.selfId = userData?.id;
  this.remotePlayers = {};
  this.userData = userData;
  this.pendingOrbTextures = {};
  this.lastBroadcast = {
    x: null,
    y: null,
    direction: null,
    animState: null,
  };

  console.log("🧠 selfId:", this.selfId);

  if (userData) {
    updatePlayerPresence(userData, {
      ...player,
      world: player?.world ?? userData?.world ?? "Void City",
      building,
      online: true,
    });
  }

  // Spawn
  const spawn = this.resolveInitialPlayerSpawn(data);
  const initialFacingDirection = spawn.facingDirection;
  this.lastFacingDirection =
    typeof initialFacingDirection === "number" ? initialFacingDirection : 0;
  console.log("📍 Using spawn position:", spawn);

  // ✅ SOLUTION A: DO NOT OFFSET THE WORLD BASED ON CAMERA SIZE
  // Keep map origin at 0,0 always.
  this.offsetX = 0;
  this.offsetY = 0;

  let mapWidth = 1000;
  let mapHeight = 1000;
  applyWorldSceneBackground(this);
  this.mapTileWidth = this.mapSource?.tilewidth || 0;
  this.mapTileHeight = this.mapSource?.tileheight || 0;
  this.mapPixelWidth =
    this.mapSource?.width && this.mapTileWidth
      ? this.mapSource.width * this.mapTileWidth
      : 0;
  this.mapPixelHeight =
    this.mapSource?.height && this.mapTileHeight
      ? this.mapSource.height * this.mapTileHeight
      : 0;

  // Background
  if (this.mapSource) {
    mapWidth = this.mapPixelWidth;
    mapHeight = this.mapPixelHeight;
    this.buildMapTilesetRegistry();
    this.drawMapFromSource();
    this.cameras.main.setBounds(0, 0, mapWidth, mapHeight);
    this.matter.world.setBounds(0, 0, mapWidth, mapHeight);
    this.cameras.main.centerOn(mapWidth / 2, mapHeight / 2);
  } else if (this.backgroundImage) {
    const bgKey = this.scene.key + "_bg";
    const bg = this.add.image(0, 0, bgKey).setOrigin(0);

    mapWidth = bg.width;
    mapHeight = bg.height;

    // ✅ World/camera bounds match the map exactly
    this.cameras.main.setBounds(0, 0, mapWidth, mapHeight);
    this.matter.world.setBounds(0, 0, mapWidth, mapHeight);

    // Optional: start centered
    this.cameras.main.centerOn(mapWidth / 2, mapHeight / 2);
  } else {
    this.add.rectangle(0, 0, mapWidth, mapHeight, 0x111111).setOrigin(0);
    this.cameras.main.setBounds(0, 0, mapWidth, mapHeight);
    this.matter.world.setBounds(0, 0, mapWidth, mapHeight);
  }

  if (this.overlayMapSource) {
    this.mapTileWidth = this.overlayMapSource.tilewidth;
    this.mapTileHeight = this.overlayMapSource.tileheight;
    this.mapPixelWidth = this.overlayMapSource.width * this.mapTileWidth;
    this.mapPixelHeight = this.overlayMapSource.height * this.mapTileHeight;
    this.buildOverlayTilesetRegistry();
    this.drawOverlayMapLayers();
  }

  // Ensure your player gets a sensible depth (before adding FG):
  if (this.player && this.player.setDepth) this.player.setDepth(500);

  // Foreground above player (no offset needed now)
  if (this.foregroundImage) {
    const fgKey = this.scene.key + "_fg";
    this.foreground = this.add
      .image(0, 0, fgKey)
      .setOrigin(0)
      .setDepth(1000)
      .setScrollFactor(1);
  }

  this.npcDialogueText = null;

  // Player setup (✅ spawn is in map coords)
  if (resolvedLocalAvatar?.isOrbAvatar) {
    const orbBody = Phaser.Physics.Matter.Matter.Bodies.circle(
      spawn.x,
      spawn.y,
      ORB_COLLISION_RADIUS,
    );
    this.player = this.matter.add.sprite(spawn.x, spawn.y, "hero", 0);
    this.player.setExistingBody(orbBody);
    Phaser.Physics.Matter.Matter.Body.scale(
      this.player.body,
      1,
      ORB_COLLISION_Y_SCALE,
    );
  } else {
    this.player = this.matter.add
      .sprite(spawn.x, spawn.y, "hero", 0)
      .setBody(heroBodySize, heroBodyOffset);
  }
  this.player
    .setScale(playerScale)
    .setDepth(500)
    .setFixedRotation();
  this.playerOrbImage = createLocalOrbAvatarImage(
    this,
    this.player,
    spawn,
    resolvedLocalAvatar,
    localOrbTextureKey,
    { depth: 500 },
  );
  this.storyCompanionFollower = createStoryCompanionFollower(this, this.player);

  // Debug player box (kept as-is)
  this.playerDebugGfx = null;
  this.debugPlayerBox = false;

  this.ensureOrbTexture = (playerData) => {
    const avatarData = resolveAvatarData(playerData);
    if (!avatarData.isOrbAvatar || !avatarData.usesStaticImageOverlay || !avatarData.image) {
      return Promise.resolve("");
    }

    const textureKey = createOrbTextureKey(
      playerData?.orbAvatar || { tokenId: playerData?.id || avatarData.normalizedName },
    );

    if (this.textures.exists(textureKey)) {
      return Promise.resolve(textureKey);
    }
    if (this.pendingOrbTextures[textureKey]) {
      return this.pendingOrbTextures[textureKey];
    }

    this.pendingOrbTextures[textureKey] = new Promise((resolve, reject) => {
      const cleanup = () => {
        this.load.off(`filecomplete-image-${textureKey}`, handleComplete);
        this.load.off("loaderror", handleError);
      };
      const handleComplete = () => {
        cleanup();
        delete this.pendingOrbTextures[textureKey];
        resolve(textureKey);
      };
      const handleError = (file) => {
        if (file?.key !== textureKey) return;
        cleanup();
        delete this.pendingOrbTextures[textureKey];
        reject(new Error(`Failed to load orb texture: ${textureKey}`));
      };

      this.load.once(`filecomplete-image-${textureKey}`, handleComplete);
      this.load.on("loaderror", handleError);
      this.load.image(textureKey, avatarData.image);
      this.load.start();
    });

    return this.pendingOrbTextures[textureKey];
  };

  // Mobile zoom logic (kept)
  const isMobile =
    this.sys.game.device.os.android || this.sys.game.device.os.iOS;
  const zoomLevel = isMobile ? 1.2 : 2.0;

  this.cameras.main
    .startFollow(this.player, true, 0.1, 0.1)
    .setZoom(zoomLevel);

  createWalkAnimations(this, "hero", avatarKey);
  if (
    typeof initialFacingDirection === "number" &&
    !resolvedLocalAvatar?.usesStaticImageOverlay
  ) {
    this.player.setFrame(
      getAvatarFrameForDirection(avatarKey, initialFacingDirection, 0),
    );
  } else if (
    typeof initialFacingDirection === "number" &&
    resolvedLocalAvatar?.usesStaticImageOverlay
  ) {
    updateLocalOrbAvatar(this, this.player, this.playerOrbImage, resolvedLocalAvatar, {
      x: spawn.x,
      y: spawn.y,
      direction: initialFacingDirection,
      depth: 500,
    });
  }

  // Input (✅ kept — I did NOT intend to remove these)
  this.joystick = { x: 0, y: 0 };
  this.cursors = this.input.keyboard.createCursorKeys();
  this.keys = this.input.keyboard.addKeys({
    W: Phaser.Input.Keyboard.KeyCodes.W,
    A: Phaser.Input.Keyboard.KeyCodes.A,
    S: Phaser.Input.Keyboard.KeyCodes.S,
    D: Phaser.Input.Keyboard.KeyCodes.D,
  });

  this.spaceKey = this.input.keyboard.addKey(
    Phaser.Input.Keyboard.KeyCodes.SPACE
  );
  this.spaceKey.on("down", () => {
    if (typeof this._onEnter === "function") {
      this._onEnter();
    }
  });

  // Collision setup
  // ✅ With Solution A, do NOT add offsetX/offsetY to collider positions.
  if (!this.collisionData || !this.collisionData.buildings) {
    console.warn("⚠️ No collisionData.buildings found");
  } else {
    this.collisionData.buildings.forEach((b) => {
      const xs = b.polygon.map((p) => p[0]);
      const ys = b.polygon.map((p) => p[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const x = minX + (maxX - minX) / 2;
      const y = minY + (maxY - minY) / 2;

      const width = maxX - minX;
      const height = maxY - minY;

      const interactionMatch =
        typeof b.name === "string" && b.name.endsWith("-interaction")
          ? b.name.replace(/-interaction$/, "")
          : null;
      const isNPC =
        !!interactionMatch ||
        (b.name &&
          [
            "receptionist",
            "merchant",
            "decksmith",
            "questmaster",
            "questboard1",
            "questboard2",
            "voidcityfrog",
          ].includes(b.name));

      const isExit = b.name === "exit";
      const npcLabelName = interactionMatch || b.name;
      const label = isNPC ? `npc_${npcLabelName}` : isExit ? "exit" : undefined;

      this.matter.add.rectangle(x, y, width, height, {
        isStatic: true,
        isSensor: isNPC || isExit,
        label,
      });

      const color = isNPC ? 0x00ff00 : isExit ? 0x0000ff : 0xff0000;

      // Interior collider debug overlays are intentionally disabled.
    });
  }

  // Proximity triggers
  this.currentNPC = null;
  this.currentDoor = null;
  this.currentDoorPos = new Phaser.Math.Vector2();

  this.matter.world.on("collisionstart", (ev) => {
    ev.pairs.forEach(({ bodyA, bodyB }) => {
      const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
      const isPlayer = bodyA === this.player.body || bodyB === this.player.body;

      if (sensor && isPlayer) {
        const label = sensor.label;

        if (label === "exit") {
          this.currentDoor = label;
          this.currentDoorPos = sensor.position;
          window.dispatchEvent(
            new CustomEvent("interact-proximity", { detail: true })
          );

          updatePlayerPresence(userData, {
            ...player,
            building: "",
          });
        } else if (label?.startsWith("npc_")) {
          const npcName = label.replace("npc_", "");
          this.currentNPC = npcName;
          window.dispatchEvent(
            new CustomEvent("npc-proximity", { detail: npcName })
          );
        }
      }
    });
  });

  this.matter.world.on("collisionend", (ev) => {
    ev.pairs.forEach(({ bodyA, bodyB }) => {
      const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
      const isPlayer = bodyA === this.player.body || bodyB === this.player.body;

      if (sensor && isPlayer) {
        const label = sensor.label;

        if (label === "exit") {
          this.currentDoor = null;
          this.currentDoorPos = null;
          window.dispatchEvent(
            new CustomEvent("interact-proximity", { detail: false })
          );
        } else if (label?.startsWith("npc_")) {
          this.currentNPC = null;
          window.dispatchEvent(
            new CustomEvent("npc-proximity", { detail: null })
          );
          if (this.npcDialogueText) {
            this.npcDialogueText.destroy();
            this.npcDialogueText = null;
          }
        }
      }
    });
  });

  this._onEnter = () => {
    if (typeof window !== "undefined" && window.__storySceneInputLocked) {
      return;
    }

    if (this.currentNPC) {
      // Prevent error: localWorld may not be ready
      if (!this.matter.world?.localWorld?.bodies) return;

      window.dispatchEvent(
        new CustomEvent("npc-dialogue", { detail: this.currentNPC })
      );

      const npcLabel = `npc_${this.currentNPC}`;
      const npcBody = this.matter.world.localWorld.bodies.find(
        (b) => b.label === npcLabel
      );

      const dialogue = this.getNpcDialogue(this.currentNPC);
      // (Your text bubble code stays commented out as you had it.)
    } else if (this.currentDoor && this.doors["exit"]) {
      const exitAttemptDetail = {
        blocked: false,
        sceneKey: this.scene.key,
      };
      window.dispatchEvent(
        new CustomEvent("story-interior-exit-attempt", {
          detail: exitAttemptDetail,
        }),
      );
      if (exitAttemptDetail.blocked) {
        return;
      }

      AudioManager.playSFX("door");

      const target = this.doors["exit"];
      let spawn = target.spawnAt || { x: 300, y: 300 };

      // Use polygon from city map if available (kept)
      const doorLabel =
        this.scene.key === "ArenaScene"
          ? "ArenaDoor"
          : this.scene.key === "MerchantScene"
            ? "MerchantsRowDoor"
            : this.scene.key === "DecksmithScene"
              ? "DecksmithDoor"
              : this.scene.key === "QuestHallScene"
                ? "QuestHallDoor"
                : null;

      if (doorLabel && target.to !== "VoidCity2Scene") {
        const polygon =
          require("../../@shared/assets/GameMaps/void-city-collisions.json").buildings.find(
            (b) => b.name === doorLabel
          )?.polygon;

        if (polygon) {
          const xs = polygon.map(([x]) => x);
          const ys = polygon.map(([, y]) => y);
          spawn = {
            x: (Math.min(...xs) + Math.max(...xs)) / 2,
            y: (Math.min(...ys) + Math.max(...ys)) / 2 + 10,
          };
        }
      }

      // Listen for player updates
      this.events.on("setPlayers", (players, selfId) => {
        console.log("🌍 [Interior] Received players update", players);
        this.playersFromReact = players || {};
        this.selfId = selfId;
      });

      // Fade out then transition
      this.cameras.main.fadeOut(300, 0, 0, 0);

      this.cameras.main.once("camerafadeoutcomplete", () => {
        this.scene.stop(this.scene.key);
        console.log("🚪 Exiting from", this.scene.key);
        console.log("📤 Spawn target for next scene:", spawn);
        this.scene.start(target.to, {
          spawn,
          heroUrl: this.heroUrl,
          heroAvatarKey: this.heroAvatarKey,
          userData: this.userData,
        });
      });
    }
  };

  // Add listener for enter presses
  window.addEventListener("press-enter", this._onEnter);

  this.cameras.main.fadeIn(300, 0, 0, 0);

  // Cleanup
  this.events.once("shutdown", () => {
    window.removeEventListener("press-enter", this._onEnter);
    this.spaceKey?.off?.("down");
    this.currentNPC = null;
    this.currentDoor = null;
    destroyStoryCompanionFollower(this.storyCompanionFollower);
    Object.values(this.remotePlayers || {}).forEach((entry) => {
      destroyRemotePlayerVisual(entry);
    });
    this.remotePlayers = {};

    if (typeof window !== "undefined") {
      window.__npcEngaged = false;
      window.dispatchEvent(new CustomEvent("npc-proximity", { detail: null }));
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false })
      );
    }
  });
}


  update() {
    if (
      typeof window !== "undefined" &&
      (window.__npcEngaged || window.__storySceneInputLocked)
    ) {
      this.player.setVelocity(0, 0);
      this.player.anims.stop();
      syncStoryCompanionFollower(this, this.storyCompanionFollower, {
        direction: this.lastFacingDirection,
      });
      return;
    }

    const speed = 2;
    let vx = 0,
      vy = 0;

    const { x: jx, y: jy } = this.joystick;
    if (jx || jy) {
      vx = jx * speed;
      vy = jy * speed;
    } else {
      if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
      else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
      if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
      else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
    }

    if (vx && vy) {
      vx *= Math.SQRT1_2;
      vy *= Math.SQRT1_2;
    }

    this.player.setVelocity(vx, vy);

    if (
      updateLocalOrbAvatar(
        this,
        this.player,
        this.playerOrbImage,
        this.localOrbAvatarData,
        { x: this.player.x, y: this.player.y, vx, depth: 500 },
      )
    ) {
      // Orb avatars use the image overlay instead of the walking spritesheet.
    } else if (Math.abs(vy) > Math.abs(vx)) {
      if (vy < 0) this.player.anims.play("walk-up", true);
      else if (vy > 0) this.player.anims.play("walk-down", true);
    } else if (vx < 0) {
      this.player.anims.play("walk-left", true);
    } else if (vx > 0) {
      this.player.anims.play("walk-right", true);
    } else this.player.anims.stop();

    if (this.player && this.playerName && this.playerNameBg) {
      const x = this.player.x;
      const y = this.player.y - 50;

      this.playerName.setPosition(x, y);

      const padding = 6;
      const textWidth = this.playerName.width;
      const textHeight = this.playerName.height;

      this.playerNameBg.clear();
      this.playerNameBg.fillStyle(0x000000, 0.5); // Black with 50% opacity
      this.playerNameBg.fillRoundedRect(
        x - textWidth / 2 - padding,
        y - textHeight - padding,
        textWidth + padding * 2,
        textHeight + padding * 2,
        8 // corner radius
      );
    }

    if (this.playersFromReact) {
      const players = this.playersFromReact || {};
      const localId = this.selfId || this.userData?.id;
      const currentPlayerRaw = players?.[localId] || {
        world: this.userData?.world || "Void City",
        building: this.scene.settings.data?.building || "",
      };
      const currentWorld = currentPlayerRaw?.world ?? this.userData?.world ?? "Void City";
      const currentBuilding =
        currentPlayerRaw?.building ?? this.scene.settings.data?.building ?? "";

      this.playersFromReact = null;

      Object.entries(players).forEach(([uid, p]) => {
        const playerWorld = p?.world ?? "Void City";
        const playerBuilding = p?.building ?? "";

        if (
          !p ||
          uid === localId ||
          typeof p.x !== "number" ||
          typeof p.y !== "number" ||
          playerWorld !== currentWorld ||
          playerBuilding !== currentBuilding
        ) {
          return;
        }

        const avatarData = resolveAvatarData(p);
        const avatarKey = normalizeAvatarName(p?.avatar?.name || "io_default");
        const spriteKey = `avatar-${avatarKey}`;
        const customOrbTextureKey =
          avatarData.isOrbAvatar && avatarData.usesStaticImageOverlay
            ? createOrbTextureKey(
                p?.orbAvatar || { tokenId: p?.id || avatarData.normalizedName },
              )
            : null;
        const existing = this.remotePlayers[uid];

        if (
          avatarData.isOrbAvatar &&
          avatarData.usesStaticImageOverlay &&
          !this.textures.exists(customOrbTextureKey)
        ) {
          this.ensureOrbTexture(p).catch((error) => {
            console.warn("Failed to load remote orb avatar texture:", error);
          });
          return;
        }

        if (!existing) {
          const sprite = this.add
            .sprite(
              p.x,
              p.y,
              avatarData.isOrbAvatar && avatarData.usesStaticImageOverlay ? "hero" : spriteKey,
              0,
            )
            .setAlpha(avatarData.isOrbAvatar && avatarData.usesStaticImageOverlay ? 0 : 0.82)
            .setDepth(p.y + 24);

          const spriteConfig = getAvatarSpriteConfig(avatarKey);
          const remoteScale =
            (spriteConfig.worldScale || 1) *
            (avatarData?.isOrbAvatar ? 1 : INTERIOR_HUMAN_SCALE_MULTIPLIER);
          sprite.setScale(remoteScale);
          if (spriteConfig.trimBottom) {
            sprite.setCrop(
              0,
              0,
              spriteConfig.frameWidth,
              spriteConfig.frameHeight - spriteConfig.trimBottom,
            );
          }

          const orbImage =
            avatarData.isOrbAvatar && avatarData.usesStaticImageOverlay
              ? this.add
                  .image(p.x, p.y, customOrbTextureKey)
                  .setDisplaySize(ORB_RENDER_SIZE, ORB_RENDER_SIZE)
                  .setDepth(p.y + 24)
                  .setAlpha(0.92)
              : null;

          const nameTagBg = this.add.graphics().setDepth(p.y + 26);
          const nameTag = this.add
            .text(p.x, p.y - REMOTE_PLAYER_NAME_OFFSET_Y, `${p.name || "Unknown"}`, {
              font: "12px Arial",
              fill: "#ffffff",
              stroke: "#000000",
              strokeThickness: 3,
            })
            .setOrigin(0.5, 1)
            .setDepth(p.y + 27);

          this.remotePlayers[uid] = { sprite, orbImage, nameTag, nameTagBg };
        }

        const remote = this.remotePlayers[uid];
        remote.sprite?.setPosition(p.x, p.y).setDepth(p.y + 24);
        remote.orbImage?.setPosition(p.x, p.y).setDepth(p.y + 24);
        remote.nameTag?.setPosition(p.x, p.y - REMOTE_PLAYER_NAME_OFFSET_Y).setDepth(p.y + 27);
        remote.nameTag?.setText(`${p.name || "Unknown"}`);
        remote.nameTagBg?.setDepth(p.y + 26);

        if (remote.nameTag && remote.nameTagBg) {
          const padding = 6;
          const textWidth = remote.nameTag.width;
          const textHeight = remote.nameTag.height;
          remote.nameTagBg.clear();
          remote.nameTagBg.fillStyle(0x000000, 0.5);
          remote.nameTagBg.fillRoundedRect(
            p.x - textWidth / 2 - padding,
            p.y - REMOTE_PLAYER_NAME_OFFSET_Y - textHeight - padding,
            textWidth + padding * 2,
            textHeight + padding * 2,
            8,
          );
        }

        if (remote.sprite && !(avatarData.isOrbAvatar && avatarData.usesStaticImageOverlay)) {
          const frameOffset =
            p.animState === "walking" ? (((Date.now() % 400) / 100) | 0) : 0;
          remote.sprite.setFrame(
            getAvatarFrameForDirection(
              p?.avatar?.name || "io_default",
              p.direction,
              frameOffset,
            ),
          );
        }
      });

      Object.keys(this.remotePlayers).forEach((uid) => {
        const p = players?.[uid];
        const playerWorld = p?.world ?? "Void City";
        const playerBuilding = p?.building ?? "";
        if (
          !p ||
          uid === localId ||
          playerWorld !== currentWorld ||
          playerBuilding !== currentBuilding
        ) {
          destroyRemotePlayerVisual(this.remotePlayers[uid]);
          delete this.remotePlayers[uid];
        }
      });
    }

    const dir =
      vx < 0
        ? 270
        : vx > 0
          ? 90
          : vy < 0
            ? 180
            : vy > 0
              ? 0
              : null;
    const animState = vx === 0 && vy === 0 ? "idle" : "walking";
    if (dir !== null) {
      this.lastFacingDirection = dir;
    }

    syncStoryCompanionFollower(this, this.storyCompanionFollower, {
      vx,
      vy,
      direction: this.lastFacingDirection,
    });

    const shouldBroadcast =
      this.player.x !== this.lastBroadcast.x ||
      this.player.y !== this.lastBroadcast.y ||
      dir !== this.lastBroadcast.direction ||
      animState !== this.lastBroadcast.animState;

    if (shouldBroadcast && this.userData?.id) {
      this.lastBroadcast = {
        x: this.player.x,
        y: this.player.y,
        direction: dir,
        animState,
      };
      updatePlayerPresence(this.userData, {
        partyId: this.userData?.partyId,
        host: this.userData?.isHost,
        inGameRoom: false,
        world: this.userData?.world ?? "Void City",
        building: this.scene.settings.data?.building ?? "",
        roomId: "",
        emote: "",
        device: "desktop",
        level: this.userData?.level,
        name: this.userData?.username,
        avatar: this.userData?.avatar || null,
        orbAvatar: this.userData?.orbAvatar || null,
        online: true,
        x: this.player.x,
        y: this.player.y,
        direction: dir ?? 0,
        animState,
      });
    }

    if (this.debugPlayerBox /* || this.debugColliders */) {
      this.drawPlayerDebugBox();
    } else if (this.playerDebugGfx) {
      this.playerDebugGfx.clear();
    }
  }
}
